import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Drawer, Menu, MenuItem, Tooltip } from '@mui/material'
import {
  CircleHelp,
  ChevronDown,
  ChevronUp,
  FileText,
  LogOut,
  Shield,
  UserRound,
  UsersRound,
} from 'lucide-react'
import V3ClientSelect from './V3ClientSelect'

type PortalItem = {
  key: string
  label: string
  to: string
  product?: 'retail' | 'indie'
  icon: React.ComponentType<any>
  matchPrefixes?: string[]
  exact?: boolean
  dividerBefore?: boolean
  badge?: string | number
}

type Props = {
  groups: { key: string; label: string; mark: string; items: PortalItem[] }[]
  adminItems?: { key: string; label: string; to: string; icon: React.ComponentType<any> }[]
  currentUser?: any
  collapsed?: boolean
  onToggleCollapsed?: () => void
  mobileOpen?: boolean
  onCloseMobile?: () => void
  mobileMode?: boolean
  showClientSelect?: boolean
  isAffiliate?: boolean
  profileHref?: string
  billingHref?: string
  onLogout?: () => void
}

const accountItems = [
  { key: 'profile', label: 'Profile', icon: UserRound },
  { key: 'billing', label: 'Account & Billing', icon: FileText },
  { key: 'help-indie', label: 'Help - Indie Insights', icon: CircleHelp },
  { key: 'help-retail', label: 'Help - RetailORBIT', icon: CircleHelp },
]

const managementOneLogo = '/management-one-logo.svg'

function isItemActive(pathname: string, item: PortalItem) {
  if (item.exact) return pathname === item.to
  return item.matchPrefixes?.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    || pathname === item.to
}

function MemberAvatar({ user }: { user?: any }) {
  const initials = `${user?.first_name?.[0] || user?.name?.[0] || user?.email?.[0] || 'U'}`.toUpperCase()
  return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C2410C] text-[12px] font-semibold text-white">{initials}</span>
}

export default function V3PortalRail({
  groups,
  adminItems = [],
  currentUser,
  collapsed = false,
  onToggleCollapsed,
  mobileOpen = false,
  onCloseMobile,
  mobileMode = false,
  showClientSelect = false,
  isAffiliate = false,
  profileHref,
  billingHref,
  onLogout,
}: Props) {
  const location = useLocation()
  const [adminAnchor, setAdminAnchor] = React.useState<HTMLElement | null>(null)
  const [accountAnchor, setAccountAnchor] = React.useState<HTMLElement | null>(null)
  const [mobileAdminOpen, setMobileAdminOpen] = React.useState(false)
  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>({})
  const width = mobileMode ? 'min(320px, 100vw)' : collapsed ? 72 : 288
  const initialsLabel = currentUser?.email || currentUser?.name || 'Member'
  const fallbackActiveKey = location.pathname.includes('/portal/') ? 'indie-dashboard' : 'retail-dashboard'
  const activeAdminItem = adminItems.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))
  const hasActiveItem = activeAdminItem || groups.some((group) => group.items.some((item) => isItemActive(location.pathname, item)))

  const renderNavItem = (item: PortalItem) => {
    const active = isItemActive(location.pathname, item) || (!hasActiveItem && item.key === fallbackActiveKey)
    const color = active ? (item.product === 'retail' ? '#6941C6' : '#9A3412') : '#344054'
    const background = active ? (item.product === 'retail' ? '#F4EBFF' : '#FFF4EA') : 'transparent'
    const Icon = item.icon
    return (
      <Tooltip key={item.key} title={collapsed && !mobileMode ? item.label : ''} placement="right" enterDelay={0} disableInteractive componentsProps={{ tooltip: { sx: { fontSize: 13, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' } } }}>
      <Link
        to={item.to}
        onClick={mobileMode ? onCloseMobile : undefined}
        aria-current={active ? 'page' : undefined}
        className={`group relative flex h-10 min-w-0 items-center gap-[11px] rounded-lg px-[10px] text-[14px] font-medium leading-5 no-underline hover:bg-[#F9FAFB] hover:text-[#101828] ${collapsed && !mobileMode ? 'justify-center px-0' : ''}`}
        style={{
          color,
          backgroundColor: background,
          fontWeight: active ? 600 : 400,
          borderRadius: active ? '12px' : '8px',
          boxShadow: active ? `inset 3px 0 0 ${item.product === 'retail' ? '#5558D4' : '#F97215'}` : undefined,
        }}
      >
        <Icon aria-hidden="true" className="h-[19px] w-[19px] shrink-0" color={active ? (item.product === 'retail' ? '#7F56D9' : '#C2410C') : '#667085'} strokeWidth={1.7} />
        {(!collapsed || mobileMode) && (
          <span className="min-w-0 flex-[0_1_auto] whitespace-normal">{item.label}</span>
        )}
        {item.badge !== undefined && !collapsed && !mobileMode && (
          <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F2F4F7] px-1.5 text-center text-[12px] font-semibold leading-[18px] text-[#344054]">{item.badge}</span>
        )}
        {collapsed && !mobileMode && item.badge !== undefined && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#9A3412]" />}
      </Link>
      </Tooltip>
    )
  }

  const rail = (
    <aside className="relative flex h-full flex-col overflow-y-auto border-r border-[#EAECF0] bg-white pb-3 text-[#344054]" style={{ width, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' }}>
      {!mobileMode && (
        <div className={`relative flex h-14 shrink-0 items-center px-4 ${collapsed ? 'justify-center px-2' : ''}`}>
          {collapsed ? (
            <svg viewBox="0 0 100 100" role="img" aria-label="Management ONE" className="h-7 w-7 shrink-0">
              <rect width="100" height="100" rx="22" fill="#664787" />
              <text x="50" y="52" textAnchor="middle" dominantBaseline="central" fontFamily="Montserrat,Arial,sans-serif" fontWeight="700" fontSize="50" fill="#FFFFFF">M</text>
            </svg>
          ) : (
            <img src={managementOneLogo} alt="Management ONE" className="h-auto max-h-[26px] w-auto max-w-[calc(100%-38px)] object-contain" />
          )}
          {!collapsed && <button type="button" aria-label="Collapse navigation" aria-expanded="true" aria-controls="portal-primary-nav" onClick={onToggleCollapsed} className="absolute right-[10px] top-[14px] inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-[#667085] hover:bg-[#F2F4F7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F97215]">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <rect x="3" y="3.5" width="18" height="17" rx="2.6" />
              <path d="M9.4 3.5v17" />
              <path d="M7.3 9.6 5.2 12l2.1 2.4" />
            </svg>
          </button>}
        </div>
      )}
      {!mobileMode && collapsed && (
        <button type="button" aria-label="Expand navigation" aria-expanded="false" aria-controls="portal-primary-nav" onClick={onToggleCollapsed} className="mx-auto mt-[6px] inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-[#667085] hover:bg-[#F2F4F7]">
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <rect x="3" y="3.5" width="18" height="17" rx="2.6" />
            <path d="M9.4 3.5v17" />
            <g transform="rotate(180 6.25 12)"><path d="M7.3 9.6 5.2 12l2.1 2.4" /></g>
          </svg>
        </button>
      )}
      {mobileMode && (
        <div className="flex h-14 shrink-0 items-center px-4">
          <img src={managementOneLogo} alt="Management ONE" className="h-[26px] w-auto max-w-full object-contain" />
        </div>
      )}
      {mobileMode && showClientSelect && (
        <div className="border-b border-[#EAECF0] p-3">
          <V3ClientSelect className="w-full" placeholder="Select a client" />
        </div>
      )}
      {mobileMode && isAffiliate && (
        <Link to="/v3/portal/coaches-corner" onClick={onCloseMobile} className="flex h-11 items-center gap-2 border-b border-[#EAECF0] px-4 text-[14px] font-medium text-[#344054] no-underline">
          <UsersRound aria-hidden="true" className="h-5 w-5" />
          Coaches' Corner
        </Link>
      )}
      <nav id="portal-primary-nav" aria-label="Portal" className="flex-1 overflow-y-auto">
        {groups.map((group) => (
          <section key={group.key} className={`px-3 pb-1 pt-[14px] ${group.key === 'indie' ? 'border-t border-[#EAECF0]' : ''}`}>
            <Tooltip title={collapsed && !mobileMode ? group.label : ''} placement="right" enterDelay={0} disableInteractive componentsProps={{ tooltip: { sx: { fontSize: 13, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' } } }}>
            <div aria-label={group.label} className={`group relative mb-1 flex h-8 items-center gap-2 px-[10px] ${collapsed && !mobileMode ? 'justify-center px-0' : ''}`}>
              {group.key === 'indie' ? (
                <img src="/indie-insights-mark.svg" alt="Indie Insights" className="h-[26px] w-[26px] shrink-0" />
              ) : (
                <svg viewBox="0 0 100 100" role="img" aria-label="Retail ORBIT" className="h-[30px] w-[30px] shrink-0">
                  <rect width="100" height="100" rx="21" fill="#7F56D9" />
                  <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontFamily="Montserrat,Arial,sans-serif" fontWeight="800" fontSize="44" letterSpacing="-1">
                    <tspan fill="#FFFFFF">R</tspan><tspan fill="#9FA2FF">O</tspan>
                  </text>
                </svg>
              )}
              {(!collapsed || mobileMode) && <span className="min-w-0 flex-[0_1_auto] whitespace-normal text-[14px] font-semibold leading-5 text-[#101828]" style={{ fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' }}>{group.label}</span>}
              {(!collapsed || mobileMode) && (
                <Tooltip title={`${collapsedSections[group.key] ? 'Expand' : 'Collapse'} ${group.label}`} placement="right" enterDelay={0}>
                  <button
                    type="button"
                    aria-label={`${collapsedSections[group.key] ? 'Expand' : 'Collapse'} ${group.label}`}
                    aria-expanded={!collapsedSections[group.key]}
                    aria-controls={`portal-section-${group.key}`}
                    onClick={() => setCollapsedSections((sections) => ({ ...sections, [group.key]: !sections[group.key] }))}
                    className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#667085] hover:bg-[#F2F4F7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C2410C]"
                  >
                    {collapsedSections[group.key]
                      ? <ChevronDown aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                      : <ChevronUp aria-hidden="true" className="h-4 w-4" strokeWidth={2} />}
                  </button>
                </Tooltip>
              )}
            </div>
            </Tooltip>
            <div id={`portal-section-${group.key}`} className="flex flex-col gap-[2px]" style={{ display: collapsedSections[group.key] && (!collapsed || mobileMode) ? 'none' : undefined }}>
              {group.items.map((item) => (
                <React.Fragment key={item.key}>
                  {item.dividerBefore && <div aria-hidden="true" className="my-[8px] border-t border-[#EAECF0]" />}
                  {renderNavItem(item)}
                </React.Fragment>
              ))}
            </div>
          </section>
        ))}
      </nav>
      {adminItems.length > 0 && (
        <div className="relative border-t border-[#EAECF0] px-3 py-2">
          <Tooltip title={collapsed && !mobileMode ? 'Admin tools' : ''} placement="right" enterDelay={0} disableInteractive componentsProps={{ tooltip: { sx: { fontSize: 13, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' } } }}>
          <button
            type="button"
            aria-haspopup={mobileMode ? undefined : 'menu'}
            aria-expanded={mobileMode ? mobileAdminOpen : Boolean(adminAnchor)}
            aria-controls={mobileMode ? 'portal-admin-submenu' : 'portal-admin-menu'}
            onClick={(event) => {
              if (mobileMode) setMobileAdminOpen((open) => !open)
              else setAdminAnchor(event.currentTarget)
            }}
            className={`flex h-10 w-full items-center gap-[11px] rounded-lg px-[10px] text-left text-[14px] font-medium text-[#344054] hover:bg-[#F9FAFB] hover:text-[#101828] ${activeAdminItem ? 'bg-[#FFF4EA] text-[#9A3412]' : ''} ${collapsed && !mobileMode ? 'justify-center px-0' : ''}`}
          >
            <Shield aria-hidden="true" className="h-[17px] w-[17px] shrink-0" strokeWidth={1.7} />
            {(!collapsed || mobileMode) && <span className="min-w-0 flex-[0_1_auto]">Admin tools</span>}
            {mobileMode && <ChevronDown aria-hidden="true" className={`ml-auto h-4 w-4 transition-transform ${mobileAdminOpen ? 'rotate-180' : ''}`} />}
          </button>
          </Tooltip>
          {!mobileMode && (
            <Menu
              id="portal-admin-menu"
              anchorEl={adminAnchor}
              open={Boolean(adminAnchor)}
              onClose={() => setAdminAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    ml: 1,
                    minWidth: 232,
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
                  },
                },
              }}
            >
              {adminItems.map((item) => {
                const Icon = item.icon
                return (
                  <MenuItem
                    key={item.key}
                    component={Link}
                    to={item.to}
                    onClick={() => setAdminAnchor(null)}
                    sx={{ gap: 1.25, minHeight: 40, px: 1.5, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}
                  >
                    <Icon aria-hidden="true" className="h-[17px] w-[17px]" strokeWidth={1.7} />
                    {item.label}
                  </MenuItem>
                )
              })}
            </Menu>
          )}
          {mobileMode && mobileAdminOpen && (
            <div id="portal-admin-submenu" className="mt-1 border-l border-[#EAECF0] pl-3">
              {adminItems.map((item) => {
                const Icon = item.icon
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={onCloseMobile}
                    aria-current={active ? 'page' : undefined}
                    className={`flex min-h-9 items-center gap-[11px] rounded-lg px-[10px] py-1 text-[13.5px] text-[#344054] no-underline ${active ? 'bg-[#F4EBFF] font-semibold text-[#6941C6]' : ''}`}
                  >
                    <Icon aria-hidden="true" className="h-[17px] w-[17px] shrink-0" strokeWidth={1.7} />
                    {item.label}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
      <div className="mt-auto border-t border-[#EAECF0] px-3 pt-[10px]">
        {onLogout && (
          <Tooltip title={collapsed && !mobileMode ? initialsLabel : ''} placement="right" enterDelay={0} disableInteractive componentsProps={{ tooltip: { sx: { fontSize: 13, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' } } }}>
          <button
            type="button"
            aria-label={`Account menu for ${initialsLabel}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(accountAnchor)}
            aria-controls="portal-account-menu"
            onClick={(event) => setAccountAnchor(event.currentTarget)}
            className={`flex h-[52px] w-full min-w-0 items-center gap-[10px] rounded-lg px-2 text-left hover:bg-[#F9FAFB] ${collapsed && !mobileMode ? 'justify-center px-0' : ''}`}
          >
            <MemberAvatar user={currentUser} />
            {(!collapsed || mobileMode) && (
              <span className="min-w-0 flex-1 leading-[17px]">
                <span className="block truncate text-[14px] font-semibold leading-5 text-[#101828]">{currentUser?.name || currentUser?.first_name || 'Member'}</span>
                {currentUser?.email && <span className="block truncate text-[12px] leading-4 text-[#475467]">{currentUser.email}</span>}
              </span>
            )}
            {(!collapsed || mobileMode) && <ChevronDown aria-hidden="true" className="h-[11px] w-[11px] shrink-0 text-[#667085]" />}
          </button>
          </Tooltip>
        )}
        <Menu
          id="portal-account-menu"
          anchorEl={accountAnchor}
          open={Boolean(accountAnchor)}
          onClose={() => setAccountAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 232, borderRadius: '10px', border: '1px solid #EAECF0', boxShadow: '0 12px 28px rgba(16,24,40,.14)' } } }}
        >
          <div className="border-b border-[#EAECF0] px-[10px] py-2 text-[12px] leading-[17px] text-[#667085]">
            <span className="block">Signed in as</span>
            <strong className="block font-bold text-[#101828]">{currentUser?.email || initialsLabel}</strong>
            {showClientSelect && <span className="mt-1 inline-block rounded-full bg-[#FFF4EA] px-[7px] py-[2px] text-[10px] font-semibold uppercase tracking-wide text-[#9A3412]">Staff</span>}
          </div>
          {accountItems.map(({ key, label, icon: Icon }) => {
            const to = key === 'profile'
              ? profileHref || '/v3/portal/profile'
              : key === 'billing'
                ? '/v3/portal/billing'
                : `/v3/portal/help?product=${key === 'help-indie' ? 'indie' : 'retail'}`
            return (
              <MenuItem key={key} component={Link} to={to} onClick={() => setAccountAnchor(null)} sx={{ gap: 1.25, minHeight: 36, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}>
                <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.7} />
                {label}
              </MenuItem>
            )
          })}
          {onLogout && <MenuItem onClick={() => { setAccountAnchor(null); onLogout() }} sx={{ minHeight: 40, fontSize: 14, color: '#344054' }}><LogOut aria-hidden="true" className="mr-3 h-4 w-4" />Logout</MenuItem>}
        </Menu>
      </div>
    </aside>
  )

  return mobileMode ? (
    <Drawer
      anchor="left"
      variant="temporary"
      open={mobileOpen}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { top: 'var(--portal-chrome-bottom, 56px)', height: 'calc(100% - var(--portal-chrome-bottom, 56px))', width: 'min(320px, 100vw)', borderRight: '1px solid #EAECF0' } }}
    >
      {rail}
    </Drawer>
  ) : (
    <div className="fixed bottom-0 left-0 top-0 z-30 hidden border-r border-[#EAECF0] min-[1000px]:block" style={{ width }}>
      {rail}
    </div>
  )
}
